import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { GradientTextAction, InputLabel, Poppins } from 'src/common';
import styled, { useTheme } from 'styled-components';
import { gql, useMutation } from '@apollo/client';
import { OrganisationQuery, useUpsertOrganisationMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import logoSvg from 'src/assets/cydea.svg';
import { CheckIcon } from 'src/assets/dynamicSvg';
import { normalizeHexColor } from 'src/utils/color';
import { useImageLightness } from 'src/hooks/useImageLightness';
import { useAuth } from 'src/state/auth';
import { BrandPreview, BrandColorsEditor, LogoUpload } from './comps';

export interface BrandingHandle {
  uploadStagedLogo(): Promise<string | null>;
  hasLogoStaged: boolean;
  isUploading: boolean;
  clearStaged(): void;
  showSuccess(title: string, text: string): void;
}

interface BrandingProps {
  organisation: NonNullable<OrganisationQuery['organisation']>;
  onLogoStateChange: (hasFile: boolean, isUploading: boolean) => void;
}

const BrandingDiv = styled.div`
  position: relative;

  .logo-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .branding-toast {
    position: absolute;
    top: 18px;
    right: 18px;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    border-radius: 8px;
    padding: 12px 16px;
    max-width: 340px;
    z-index: 5;
  }

  .branding-toast__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .branding-toast__title {
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.prussianBlue};
  }

  .branding-toast__text {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.cflowerBlue};
    line-height: 1.4;
  }

  .logo-shadow {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3)) drop-shadow(0 0 1px rgba(0, 0, 0, 0.25));
  }

  .logo-colors {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    position: relative;
  }

  .logo-primary {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    position: relative;

    &__row {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    &__preview {
      position: relative;
      display: inline-flex;
      align-items: center;
    }

    &__swatches {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__swatch {
      width: 22px;
      height: 22px;
      border-radius: 4px;
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      background-color: transparent;
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.white};
    }

    &__swatch--empty {
      background-color: ${({ theme }) => theme.colors.white};
      background-image: linear-gradient(
          45deg,
          ${({ theme }) => theme.colors.stroke} 25%,
          transparent 25%,
          transparent 75%,
          ${({ theme }) => theme.colors.stroke} 75%,
          ${({ theme }) => theme.colors.stroke}
        ),
        linear-gradient(
          45deg,
          ${({ theme }) => theme.colors.stroke} 25%,
          transparent 25%,
          transparent 75%,
          ${({ theme }) => theme.colors.stroke} 75%,
          ${({ theme }) => theme.colors.stroke}
        );
      background-size: 6px 6px;
      background-position:
        0 0,
        3px 3px;
      background-repeat: repeat;
      background-origin: border-box;
      background-clip: border-box;
    }
  }
`;

export const Branding = React.forwardRef<BrandingHandle, BrandingProps>(({ organisation, onLogoStateChange }, ref) => {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isRemovingLogo, setIsRemovingLogo] = useState(false);
  const [showBrandColorsEditor, setShowBrandColorsEditor] = useState(false);
  const [showBrandPreview, setShowBrandPreview] = useState(false);
  const [primaryColorDraft, setPrimaryColorDraft] = useState('');
  const [primaryColorError, setPrimaryColorError] = useState<string | null>(null);
  const [isUpdatingColors, setIsUpdatingColors] = useState(false);
  const [secondaryColorDraft, setSecondaryColorDraft] = useState('');
  const [secondaryColorError, setSecondaryColorError] = useState<string | null>(null);
  const [brandingSuccessMessage, setBrandingSuccessMessage] = useState<{ title: string; text: string } | null>(null);

  const { features } = useAuth();
  const theme = useTheme();
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const brandingSuccessTimeoutRef = useRef<number | null>(null);

  const { refetchQuery } = useCache();
  const maxLogoSizeBytes = 100 * 1024;
  const canEditSettings = organisation.actionsOnThisOrganisation?.canEditSettings;
  const brandingEnabled = features['app.partner.branding'];
  const canEditBranding = Boolean(canEditSettings && brandingEnabled);

  const refetch = () => refetchQuery({ include: ['organisation', 'flatOrganisationsHome', 'flatOrganisationsNav'] });

  const [update] = useUpsertOrganisationMutation();
  const [requestLogoUpload] = useMutation(gql`
    mutation requestLogoUpload($contentType: String!, $extension: String!) {
      requestLogoUpload(contentType: $contentType, extension: $extension) {
        uploadUrl
        key
        publicUrl
      }
    }
  `);

  const getLogoExtension = (file: File) => {
    const nameExtension = file.name.split('.').pop()?.toLowerCase() || '';
    if (nameExtension) return nameExtension;
    if (file.type === 'image/png') return 'png';
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') return 'jpg';
    return '';
  };

  const applyLogoFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setLogoFile(file);
      setLogoPreviewUrl(reader.result as string);
      setLogoError(null);
    };
    reader.onerror = () => {
      setLogoError('Unable to read logo file.');
    };
    reader.readAsDataURL(file);
  };

  const clearLogoSelection = () => {
    setLogoFile(null);
    setLogoPreviewUrl(null);
  };

  const handleOpenBrandColorsEditor = () => {
    if (!canEditBranding) return;
    setPrimaryColorDraft(organisation.primaryColor || '');
    setPrimaryColorError(null);
    setSecondaryColorDraft(organisation.secondaryColor || '');
    setSecondaryColorError(null);
    setShowBrandColorsEditor(true);
  };

  const handleCloseBrandColorsEditor = () => {
    setShowBrandColorsEditor(false);
    setSecondaryColorDraft(organisation.secondaryColor || '');
    setSecondaryColorError(null);
    setPrimaryColorDraft(organisation.primaryColor || '');
    setPrimaryColorError(null);
  };

  const handleResetBrandColorsEditor = () => {
    setPrimaryColorDraft('');
    setSecondaryColorDraft('');
    setPrimaryColorError(null);
    setSecondaryColorError(null);
  };

  const handleLogoPicker = () => {
    if (!canEditBranding) return;
    setLogoError(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
      logoInputRef.current.click();
    }
  };

  const handleLogoInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditBranding) return;
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > maxLogoSizeBytes) {
      setLogoError('Logo must be smaller than 100 KB.');
      clearLogoSelection();
      return;
    }

    const extension = getLogoExtension(file);
    if (!extension) {
      setLogoError('Only PNG or JPEG files are supported.');
      clearLogoSelection();
      return;
    }

    applyLogoFile(file);
  };

  const handleRemoveLogo = async () => {
    if (!canEditBranding || !organisation.logoUrl || isRemovingLogo) return;
    setLogoError(null);
    setIsRemovingLogo(true);
    try {
      await update({ variables: { organisationId: organisation.id, logoKey: null } });
      await refetch();
      clearLogoSelection();
      showBrandingSuccess('Logo removed successfully!', 'Enter your workspace to see the changes made.');
    } catch (removeError) {
      setLogoError('Unable to remove logo. Please try again.');
    } finally {
      setIsRemovingLogo(false);
    }
  };

  const showBrandingSuccess = (title: string, text: string) => {
    setBrandingSuccessMessage({ title, text });
    if (brandingSuccessTimeoutRef.current) {
      window.clearTimeout(brandingSuccessTimeoutRef.current);
    }
    brandingSuccessTimeoutRef.current = window.setTimeout(() => {
      setBrandingSuccessMessage(null);
    }, 5000);
  };

  useEffect(() => {
    if (!showBrandColorsEditor) {
      setPrimaryColorDraft(organisation.primaryColor || '');
      setPrimaryColorError(null);
      setSecondaryColorDraft(organisation.secondaryColor || '');
      setSecondaryColorError(null);
    }
  }, [organisation.primaryColor, organisation.secondaryColor, showBrandColorsEditor]);

  useEffect(
    () => () => {
      if (brandingSuccessTimeoutRef.current) {
        window.clearTimeout(brandingSuccessTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    onLogoStateChange(Boolean(logoFile), isUploadingLogo);
  }, [logoFile, isUploadingLogo, onLogoStateChange]);

  const currentLogoIsLight = useImageLightness(organisation.logoUrl);
  const previewLogoIsLight = useImageLightness(logoPreviewUrl);

  const handleBrandColorsUpdate = async () => {
    if (!canEditBranding || isUpdatingColors) return;
    setPrimaryColorError(null);
    setSecondaryColorError(null);

    const primaryTrimmed = primaryColorDraft.trim();
    let normalizedPrimary: string | null = null;
    if (primaryTrimmed) {
      normalizedPrimary = normalizeHexColor(primaryTrimmed);
      if (!normalizedPrimary) {
        setPrimaryColorError('Enter a valid hex code (e.g. #1A2B3C).');
        return;
      }
    }

    const secondaryTrimmed = secondaryColorDraft.trim();
    let normalizedSecondary: string | null = null;
    if (secondaryTrimmed) {
      normalizedSecondary = normalizeHexColor(secondaryTrimmed);
      if (!normalizedSecondary) {
        setSecondaryColorError('Enter a valid hex code (e.g. #1A2B3C).');
        return;
      }
    }

    setIsUpdatingColors(true);
    try {
      await update({
        variables: {
          organisationId: organisation.id,
          primaryColor: normalizedPrimary,
          secondaryColor: normalizedSecondary,
        },
      });
      await refetch();
      setShowBrandColorsEditor(false);
      showBrandingSuccess('Brand colours updated successfully!', 'Enter your workspace to see the changes made.');
    } catch (colorError) {
      setPrimaryColorError('Unable to update brand colours. Please try again.');
    } finally {
      setIsUpdatingColors(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      get hasLogoStaged() {
        return Boolean(logoFile);
      },
      get isUploading() {
        return isUploadingLogo;
      },
      clearStaged: clearLogoSelection,
      showSuccess: showBrandingSuccess,
      uploadStagedLogo: async () => {
        const pendingLogoFile = logoFile;
        if (!pendingLogoFile) return null;

        setLogoError(null);
        if (pendingLogoFile.size > maxLogoSizeBytes) {
          setLogoError('Logo must be smaller than 100 KB.');
          return null;
        }

        const extension = getLogoExtension(pendingLogoFile);
        if (!extension) {
          setLogoError('Unsupported file type.');
          return null;
        }

        const contentType = pendingLogoFile.type || (extension === 'png' ? 'image/png' : 'image/jpeg');
        setIsUploadingLogo(true);
        try {
          const { data } = await requestLogoUpload({
            variables: {
              contentType,
              extension,
            },
          });

          const uploadUrl = data?.requestLogoUpload?.uploadUrl;
          const logoKey = data?.requestLogoUpload?.key;
          if (!uploadUrl || !logoKey) {
            setLogoError('Unable to request logo upload.');
            return null;
          }

          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': contentType,
            },
            body: pendingLogoFile,
          });

          if (!uploadResponse.ok) {
            setLogoError('Logo upload failed. Please try again.');
            return null;
          }

          return logoKey;
        } catch (uploadError) {
          setLogoError('Logo upload failed. Please try again.');
          return null;
        } finally {
          setIsUploadingLogo(false);
        }
      },
    }),
    [logoFile, isUploadingLogo, requestLogoUpload],
  );

  const isPartnerOrg = organisation.organisationType === 'PARTNER';
  const hasOrganisationLogo = Boolean(organisation.logoUrl);
  const hasLogoPreview = Boolean(logoPreviewUrl);
  const displayLogoUrl = logoPreviewUrl || organisation.logoUrl || logoSvg;
  const currentPrimaryColor = normalizeHexColor(organisation.primaryColor);
  const currentSecondaryColor = normalizeHexColor(organisation.secondaryColor);
  const canRemoveLogo = hasOrganisationLogo && canEditBranding && !isRemovingLogo;
  const canUpdateBrandColors = Boolean(canEditBranding) && !isUpdatingColors;
  const previewLogoSrc = logoPreviewUrl;
  const poweredLogoSrc = previewLogoSrc || displayLogoUrl;
  const previewPrimaryColor =
    normalizeHexColor(showBrandColorsEditor ? primaryColorDraft : organisation.primaryColor) ||
    currentPrimaryColor ||
    theme.colors.gp1;
  const previewSecondaryColor =
    normalizeHexColor(showBrandColorsEditor ? secondaryColorDraft : organisation.secondaryColor) ||
    currentSecondaryColor ||
    theme.colors.gp1;
  const currentLogoShadow = hasLogoPreview
    ? Boolean(previewLogoIsLight ?? true)
    : Boolean(organisation.logoUrl) && (currentLogoIsLight ?? true);
  const previewLogoShadow = Boolean(logoPreviewUrl) && (previewLogoIsLight ?? true);
  const poweredLogoShadow = logoPreviewUrl ? previewLogoShadow : currentLogoShadow;

  if (!isPartnerOrg) return null;

  return (
    <BrandingDiv>
      {brandingSuccessMessage && (
        <div className="branding-toast">
          <CheckIcon color="success" />
          <div className="branding-toast__content">
            <div className="branding-toast__title">{brandingSuccessMessage.title}</div>
            <div className="branding-toast__text">{brandingSuccessMessage.text}</div>
          </div>
        </div>
      )}
      <div className="logo-section">
        <InputLabel>PARTNER LOGO</InputLabel>
        <LogoUpload
          displayLogoUrl={displayLogoUrl}
          hasOrganisationLogo={hasOrganisationLogo}
          hasLogoPreview={hasLogoPreview}
          currentLogoShadow={currentLogoShadow}
          canEditBranding={canEditBranding}
          canRemoveLogo={canRemoveLogo}
          brandingEnabled={brandingEnabled}
          isRemovingLogo={isRemovingLogo}
          logoInputRef={logoInputRef}
          onPickLogo={handleLogoPicker}
          onRemoveLogo={handleRemoveLogo}
          onFileChange={handleLogoInputChange}
        >
          <div className="logo-colors">
            <div className="logo-primary">
              <div className="logo-primary__row">
                <div className="logo-primary__swatches">
                  <span
                    className={['logo-primary__swatch', !currentPrimaryColor && 'logo-primary__swatch--empty']
                      .filter(Boolean)
                      .join(' ')}
                    style={currentPrimaryColor ? { backgroundColor: currentPrimaryColor } : undefined}
                  />
                  <span
                    className={['logo-primary__swatch', !currentSecondaryColor && 'logo-primary__swatch--empty']
                      .filter(Boolean)
                      .join(' ')}
                    style={currentSecondaryColor ? { backgroundColor: currentSecondaryColor } : undefined}
                  />
                </div>
                {brandingEnabled && (
                  <GradientTextAction
                    $underline
                    $disabled={!canEditBranding}
                    px={14}
                    weight={500}
                    onClick={handleOpenBrandColorsEditor}
                  >
                    Update brand colours
                  </GradientTextAction>
                )}
                {brandingEnabled && (
                  <div className="logo-primary__preview">
                    <GradientTextAction
                      $underline
                      $disabled={!canEditBranding}
                      px={14}
                      weight={500}
                      onClick={() => {
                        if (!canEditBranding) return;
                        setShowBrandPreview((prev) => !prev);
                      }}
                    >
                      Preview PDF & email
                    </GradientTextAction>
                    {showBrandPreview && (
                      <BrandPreview
                        primaryColor={previewPrimaryColor}
                        secondaryColor={previewSecondaryColor}
                        logoSrc={poweredLogoSrc}
                        logoShadow={poweredLogoShadow}
                        orgName={organisation.name}
                      />
                    )}
                  </div>
                )}
              </div>
              {brandingEnabled && showBrandColorsEditor && (
                <BrandColorsEditor
                  primaryColorDraft={primaryColorDraft}
                  secondaryColorDraft={secondaryColorDraft}
                  primaryColorError={primaryColorError}
                  secondaryColorError={secondaryColorError}
                  currentPrimaryColor={currentPrimaryColor}
                  currentSecondaryColor={currentSecondaryColor}
                  isUpdatingColors={isUpdatingColors}
                  canEditBranding={canEditBranding}
                  canUpdateBrandColors={canUpdateBrandColors}
                  onPrimaryChange={(value) => {
                    setPrimaryColorDraft(value);
                    if (primaryColorError) {
                      setPrimaryColorError(null);
                    }
                  }}
                  onSecondaryChange={(value) => {
                    setSecondaryColorDraft(value);
                    if (secondaryColorError) {
                      setSecondaryColorError(null);
                    }
                  }}
                  onClose={handleCloseBrandColorsEditor}
                  onReset={handleResetBrandColorsEditor}
                  onUpdate={handleBrandColorsUpdate}
                />
              )}
            </div>
          </div>
        </LogoUpload>

        {logoError && (
          <Poppins px={12} color="error">
            {logoError}
          </Poppins>
        )}
      </div>
    </BrandingDiv>
  );
});
